import * as React from "react";
import { useTranslation } from "react-i18next";
import { reportError } from "../utils/errorReporting.ts";
import {
  trackPanelCrashed,
  type CrashPanel,
} from "../services/analyticsService.ts";
import { ExclamationTriangleIcon } from "./icons.tsx";

/**
 * Error boundary scoped to a single studio panel.
 *
 * It catches render errors that ORIGINATE inside the wrapped subtree — a
 * throw in the Studio panel's or the image viewer's own components — so the
 * rest of the studio (product grid, gallery, header) stays mounted and the
 * panel offers a reset that remounts its subtree with fresh state.
 *
 * It does not, and cannot, catch an error thrown above it. A render loop in
 * the project layout's own effects (React error #185, "Maximum update depth
 * exceeded", which is where the loops this boundary was introduced alongside
 * actually lived) surfaces at `ProjectStudio`, so it is handled by the
 * project route's `errorComponent` in `projects.$projectId.tsx` instead. Both
 * report through the same `reportPanelCrash`, and both replace the global
 * `Sentry.ErrorBoundary` in `index.tsx` as the catcher, which would otherwise
 * swap the entire application for a bare "An error has occurred" paragraph.
 *
 * This is deliberately not built on `Sentry.ErrorBoundary`: that has no
 * `resetKeys`, and keying it on them would remount the panel on every project
 * or image-type switch even when nothing failed, discarding the panel's state.
 */
interface PanelErrorBoundaryProps {
  children: React.ReactNode;
  /** Identifies the panel in the error report. */
  panel: CrashPanel;
  /** Rendered when a child threw. Receives the reset callback. */
  renderFallback: (reset: () => void) => React.ReactNode;
  /**
   * Values that scope the error. When any of them changes, the boundary
   * clears itself — switching project or image is its own recovery, so a
   * panel that failed for one of them must not stay broken for the next.
   */
  resetKeys?: unknown[];
}

interface PanelErrorBoundaryState {
  error: Error | null;
}

const keysChanged = (a: unknown[] = [], b: unknown[] = []): boolean =>
  a.length !== b.length ||
  a.some((value, index) => !Object.is(value, b[index]));

/**
 * Reports a caught render error to PostHog (with the component stack, which
 * the minified production stack traces lack) and to Sentry. Shared by the
 * panel boundaries and the project route's `onCatch`.
 */
export const reportPanelCrash = (
  panel: CrashPanel,
  error: unknown,
  componentStack?: string | null
): void => {
  const context = { panel, componentStack };
  trackPanelCrashed(error, context);
  // Neither reporter may turn the caught render error into a second one:
  // the fallback has to stay usable even if telemetry is down.
  try {
    reportError(error, context);
  } catch {
    // Swallowed on purpose.
  }
};

class PanelErrorBoundaryInner extends React.Component<
  PanelErrorBoundaryProps,
  PanelErrorBoundaryState
> {
  state: PanelErrorBoundaryState = { error: null };

  static getDerivedStateFromError(
    error: Error
  ): Partial<PanelErrorBoundaryState> {
    return { error };
  }

  componentDidUpdate(previous: PanelErrorBoundaryProps) {
    if (
      this.state.error &&
      keysChanged(previous.resetKeys, this.props.resetKeys)
    ) {
      this.reset();
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    reportPanelCrash(this.props.panel, error, info.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    // While the fallback shows, the children are unmounted, so a reset mounts
    // them fresh — the failed subtree's state (where an update loop lives) is
    // discarded without any extra keying.
    if (this.state.error) return this.props.renderFallback(this.reset);
    return this.props.children;
  }
}

/**
 * Default fallback: a contained notice with a retry that resets the panel.
 * `onDismiss` adds a secondary way out, for panels the user cannot navigate
 * away from while they are broken (the full-screen image viewer).
 */
export const PanelErrorFallback: React.FC<{
  reset: () => void;
  onDismiss?: () => void;
}> = ({ reset, onDismiss }) => {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="m-4 rounded-xl border border-amber-200 bg-amber-50 p-4"
    >
      <div className="flex items-start gap-2">
        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-amber-900">
            {t("errors.panelCrashedTitle")}
          </p>
          <p className="mt-1 text-xs leading-snug text-amber-800">
            {t("errors.panelCrashedHint")}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={reset}
              className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700"
            >
              {t("errors.panelCrashedRetry")}
            </button>
            {onDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                className="rounded-lg border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-900 transition-colors hover:bg-amber-100"
              >
                {t("common.close")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Full-screen variant of the fallback, for a panel that IS a modal (the image
 * viewer). When the viewer crashes it unmounts, and its document-level
 * keyboard handling goes with it, so this brings the dialog semantics itself:
 * focus moves in on mount and back out on unmount, Escape dismisses, and Tab
 * stays inside — the same contract as `MeasurementImportDialog`.
 */
export const PanelErrorDialog: React.FC<{
  reset: () => void;
  onDismiss: () => void;
}> = ({ reset, onDismiss }) => {
  const { t } = useTranslation();
  const dialog = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialog.current?.focus();
    return () => {
      previous?.focus();
    };
  }, []);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-8 backdrop-blur-sm">
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label={t("errors.panelCrashedTitle")}
        tabIndex={-1}
        className="w-full max-w-md rounded-2xl bg-white p-1 outline-none"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            onDismiss();
          }
          if (event.key === "Tab") {
            const elements = Array.from(
              dialog.current?.querySelectorAll<HTMLElement>(
                "button:not(:disabled)"
              ) ?? []
            );
            const first = elements[0],
              last = elements.at(-1);
            if (
              event.shiftKey &&
              (document.activeElement === first ||
                document.activeElement === dialog.current)
            ) {
              event.preventDefault();
              last?.focus();
            }
            if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          }
        }}
      >
        <PanelErrorFallback reset={reset} onDismiss={onDismiss} />
      </div>
    </div>
  );
};

const PanelErrorBoundary: React.FC<
  Omit<PanelErrorBoundaryProps, "renderFallback"> & {
    renderFallback?: (reset: () => void) => React.ReactNode;
  }
> = ({ renderFallback, ...props }) => (
  <PanelErrorBoundaryInner
    {...props}
    renderFallback={
      renderFallback ?? ((reset) => <PanelErrorFallback reset={reset} />)
    }
  />
);

export default PanelErrorBoundary;
